import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
    FormHelperText,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createInterstitialCampaign,
    getInterstitialCampaign,
    getInterstitialTriggerRegistry,
    getPresentationScreenRegistry,
    getUserTags,
    updateInterstitialCampaign,
} from '../Api';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import WebGameTemplateHtmlEditorDialog from '../components/WebGameTemplateHtmlEditorDialog';
import HtmlTemplatePreview from '../components/HtmlTemplatePreview';
import type {
    InterstitialCampaignDto,
    InterstitialCampaignUpsertRequest,
    InterstitialCreativeType,
    InterstitialFrequencyMode,
} from '../models/dtos/InterstitialCampaignDto';
import type { PresentationScreenRegistryDto } from '../models/dtos/PresentationScreenRegistryDto';
import { BuildNumberAutocomplete, useAppVersionsForOsTypes } from '../utils/appVersionFields';

const CREATIVE_TYPES: InterstitialCreativeType[] = ['HTML', 'VIDEO', 'HTML_WITH_VIDEO'];
const FREQUENCY_MODES: InterstitialFrequencyMode[] = ['ONE_TIME', 'N_PER_DAY', 'UNLIMITED_WITH_COOLDOWN'];

const emptyForm = (): InterstitialCampaignUpsertRequest => ({
    slug: '',
    name: '',
    enabled: false,
    priority: 0,
    creativeType: 'HTML',
    htmlContent: '',
    remoteUrl: '',
    htmlTemplateId: '',
    videoUrl: '',
    skipAfterSeconds: 5,
    templateMeta: {},
    triggerEvents: [],
    screenKeys: [],
    userTags: [],
    tourCompletion: 'any',
    osTypes: ['android'],
    minBuild: null,
    maxBuild: null,
    frequencyMode: 'ONE_TIME',
    maxImpressionsPerUser: 1,
    maxImpressionsPerDay: 1,
    cooldownMinutes: 0,
    startAt: null,
    endAt: null,
    allowSkip: false,
    requireClickToClose: false,
});

const toIsoOrNull = (value: string): string | null => {
    if (!value.trim()) return null;
    return new Date(value).toISOString();
};

const fromIsoToLocalInput = (value: string | null | undefined): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const campaignToForm = (campaign: InterstitialCampaignDto): InterstitialCampaignUpsertRequest => ({
    slug: campaign.slug,
    name: campaign.name,
    enabled: campaign.enabled,
    priority: campaign.priority,
    creativeType: campaign.creativeType,
    htmlContent: campaign.htmlContent ?? '',
    remoteUrl: campaign.remoteUrl ?? '',
    htmlTemplateId: campaign.htmlTemplateId ?? '',
    videoUrl: campaign.videoUrl ?? '',
    skipAfterSeconds: campaign.skipAfterSeconds,
    templateMeta: campaign.templateMeta ?? {},
    triggerEvents: campaign.triggerEvents,
    screenKeys: campaign.screenKeys,
    userTags: campaign.userTags,
    tourCompletion: campaign.tourCompletion,
    osTypes: campaign.osTypes.length > 0 ? campaign.osTypes : ['android'],
    minBuild: campaign.minBuild,
    maxBuild: campaign.maxBuild,
    frequencyMode: campaign.frequencyMode,
    maxImpressionsPerUser: campaign.maxImpressionsPerUser,
    maxImpressionsPerDay: campaign.maxImpressionsPerDay,
    cooldownMinutes: campaign.cooldownMinutes,
    startAt: campaign.startAt,
    endAt: campaign.endAt,
    allowSkip: campaign.allowSkip,
    requireClickToClose: campaign.requireClickToClose,
});

export default function InterstitialCampaignEditPage() {
    const id = useParams()['id'] as string | undefined;
    const isEditMode = id != null;
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(isEditMode);
    const [saving, setSaving] = React.useState(false);
    const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);
    const [triggerEvents, setTriggerEvents] = React.useState<string[]>([]);
    const [userTags, setUserTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [screenRegistry, setScreenRegistry] = React.useState<PresentationScreenRegistryDto | null>(null);
    const [form, setForm] = React.useState<InterstitialCampaignUpsertRequest>(emptyForm());
    const [htmlEditorOpen, setHtmlEditorOpen] = React.useState(false);
    const { versions: appVersions, loading: appVersionsLoading } = useAppVersionsForOsTypes(form.osTypes);

    const screenOptions = React.useMemo(() => {
        const screens = screenRegistry?.screens ?? [];
        return screens.map(s => s.screenKey).filter(Boolean);
    }, [screenRegistry]);

    const showToast = (message: string, severity: 'success' | 'error' = 'success') => {
        setToast({ message, severity });
    };

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [registry, tags, triggers, campaign] = await Promise.all([
                getPresentationScreenRegistry(),
                getUserTags(),
                getInterstitialTriggerRegistry(),
                isEditMode && id ? getInterstitialCampaign(id) : Promise.resolve(null),
            ]);
            setScreenRegistry(registry);
            setUserTags(tags ?? []);
            setTriggerEvents(triggers?.events ?? []);
            if (campaign) {
                setForm(campaignToForm(campaign));
            } else if (isEditMode) {
                showToast('Campaign not found.', 'error');
            }
            setLoading(false);
        };
        load();
    }, [id, isEditMode]);

    const handleSave = async () => {
        setSaving(true);
        const payload: InterstitialCampaignUpsertRequest = {
            ...form,
            htmlContent: form.htmlContent?.trim() || null,
            remoteUrl: form.remoteUrl?.trim() || null,
            htmlTemplateId: form.htmlTemplateId?.trim() || null,
            videoUrl: form.videoUrl?.trim() || null,
            startAt: form.startAt ? toIsoOrNull(fromIsoToLocalInput(form.startAt)) : null,
            endAt: form.endAt ? toIsoOrNull(fromIsoToLocalInput(form.endAt)) : null,
        };
        const result = isEditMode && id
            ? await updateInterstitialCampaign(id, payload)
            : await createInterstitialCampaign(payload);
        setSaving(false);
        if (!result.ok) {
            showToast(result.error ?? 'Save failed.', 'error');
            return;
        }
        const saved = result.data;
        setForm(campaignToForm(saved));
        showToast(isEditMode ? 'Campaign updated.' : 'Campaign created.', 'success');
        if (!isEditMode) {
            navigate('/marketing/interstitials/' + encodeURIComponent(saved.id) + '/edit', { replace: true });
        }
    };

    const needsHtml = form.creativeType === 'HTML' || form.creativeType === 'HTML_WITH_VIDEO';
    const needsVideo = form.creativeType === 'VIDEO' || form.creativeType === 'HTML_WITH_VIDEO';

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Typography variant="h4">
                        {isEditMode ? 'Edit interstitial campaign' : 'New interstitial campaign'}
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate('/marketing/interstitials')}>
                        Back to campaigns
                    </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Full-screen HTML or video promos triggered by app events. Separate from AdMob interstitials and presentation overlays.
                </Typography>

                {loading ? (
                    <CircularProgress />
                ) : (
                    <Card>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Name" fullWidth value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField label="Slug" fullWidth value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Creative type</InputLabel>
                                        <Select
                                            label="Creative type"
                                            value={form.creativeType}
                                            onChange={e => setForm({ ...form, creativeType: e.target.value as InterstitialCreativeType })}
                                        >
                                            {CREATIVE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Priority" type="number" fullWidth value={form.priority} onChange={e => setForm({ ...form, priority: Number(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />} label="Enabled" />
                                </Grid>

                                {needsHtml && (
                                    <>
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="subtitle2">HTML content</Typography>
                                                <Button size="small" onClick={() => setHtmlEditorOpen(true)}>Open editor</Button>
                                            </Stack>
                                            <TextField multiline minRows={6} fullWidth value={form.htmlContent ?? ''} onChange={e => setForm({ ...form, htmlContent: e.target.value })} sx={{ mt: 1 }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField label="Remote URL" fullWidth value={form.remoteUrl ?? ''} onChange={e => setForm({ ...form, remoteUrl: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <HtmlTemplatePreview htmlContent={form.htmlContent ?? ''} />
                                        </Grid>
                                    </>
                                )}

                                {needsVideo && (
                                    <Grid item xs={12} md={8}>
                                        <TextField label="Video URL" fullWidth value={form.videoUrl ?? ''} onChange={e => setForm({ ...form, videoUrl: e.target.value })} />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Display behavior</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={<Switch checked={form.allowSkip} onChange={e => setForm({ ...form, allowSkip: e.target.checked })} />}
                                        label="Allow skip"
                                    />
                                    <FormHelperText>
                                        ON: × closes immediately. OFF: × unlocks only when countdown reaches 0.
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        label="Countdown seconds (HTML)"
                                        type="number"
                                        fullWidth
                                        value={form.skipAfterSeconds ?? ''}
                                        onChange={e => setForm({ ...form, skipAfterSeconds: Number(e.target.value) })}
                                    />
                                    <FormHelperText>
                                        Always shown in HTML. Controls when × becomes active if Allow skip is OFF.
                                    </FormHelperText>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControlLabel
                                        control={<Switch checked={form.requireClickToClose} onChange={e => setForm({ ...form, requireClickToClose: e.target.checked })} />}
                                        label="Require click to close"
                                    />
                                    <FormHelperText>
                                        ON: × only after Install / Learn more is tapped.
                                    </FormHelperText>
                                </Grid>

                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={triggerEvents}
                                        value={form.triggerEvents}
                                        onChange={(_, value) => setForm({ ...form, triggerEvents: value })}
                                        renderInput={params => <TextField {...params} label="Trigger events" />}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={screenOptions}
                                        value={form.screenKeys}
                                        onChange={(_, value) => setForm({ ...form, screenKeys: value })}
                                        renderInput={params => <TextField {...params} label="Screen keys (optional)" />}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={userTags.map(t => t.tagName)}
                                        value={form.userTags}
                                        onChange={(_, value) => setForm({ ...form, userTags: value })}
                                        renderInput={params => <TextField {...params} label="User tags" />}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Autocomplete
                                        multiple
                                        options={['android', 'ios']}
                                        value={form.osTypes}
                                        onChange={(_, value) => setForm({ ...form, osTypes: value })}
                                        renderInput={params => <TextField {...params} label="OS types" />}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <BuildNumberAutocomplete
                                        label="Min build"
                                        value={form.minBuild ?? null}
                                        onChange={minBuild => setForm({ ...form, minBuild })}
                                        versions={appVersions}
                                        loading={appVersionsLoading}
                                        peerMax={form.maxBuild ?? null}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <BuildNumberAutocomplete
                                        label="Max build"
                                        value={form.maxBuild ?? null}
                                        onChange={maxBuild => setForm({ ...form, maxBuild })}
                                        versions={appVersions}
                                        loading={appVersionsLoading}
                                        peerMin={form.minBuild ?? null}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Frequency mode</InputLabel>
                                        <Select label="Frequency mode" value={form.frequencyMode} onChange={e => setForm({ ...form, frequencyMode: e.target.value as InterstitialFrequencyMode })}>
                                            {FREQUENCY_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Max impressions / user" type="number" fullWidth value={form.maxImpressionsPerUser} onChange={e => setForm({ ...form, maxImpressionsPerUser: Number(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Max impressions / day" type="number" fullWidth value={form.maxImpressionsPerDay} onChange={e => setForm({ ...form, maxImpressionsPerDay: Number(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Cooldown (minutes)" type="number" fullWidth value={form.cooldownMinutes} onChange={e => setForm({ ...form, cooldownMinutes: Number(e.target.value) })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Start at" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={fromIsoToLocalInput(form.startAt ?? undefined)} onChange={e => setForm({ ...form, startAt: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField label="End at" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={fromIsoToLocalInput(form.endAt ?? undefined)} onChange={e => setForm({ ...form, endAt: e.target.value })} />
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions>
                            <Button variant="contained" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                            {isEditMode && (
                                <Button onClick={() => navigate('/marketing/interstitials/' + encodeURIComponent(id!) + '/stats')}>
                                    View stats
                                </Button>
                            )}
                        </CardActions>
                    </Card>
                )}
            </Stack>

            <WebGameTemplateHtmlEditorDialog
                open={htmlEditorOpen}
                onClose={() => setHtmlEditorOpen(false)}
                value={form.htmlContent ?? ''}
                onChange={value => setForm({ ...form, htmlContent: value })}
                gameCode={form.slug || 'interstitial-campaign'}
                templateFileName={form.slug ? `${form.slug}.html` : undefined}
                loading={false}
                saving={false}
                message={null}
                canReset={false}
                onSave={() => setHtmlEditorOpen(false)}
                onReloadFromServer={() => undefined}
            />

            <Snackbar
                open={toast != null}
                autoHideDuration={3500}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setToast(null)}
                    severity={toast?.severity ?? 'success'}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {toast?.message ?? ''}
                </Alert>
            </Snackbar>
        </Box>
    );
}
